//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import {
  connect,
  getConnectionStatus,
  getOAuthUrl,
  revokeToken,
} from '@apis/tiktok-settings-ads/tiktokSettingsAdsApi';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { FIVE_MINUTES_STALE_TIME } from '@queries/QueryOptionsConstants';
import { navigateToState } from '@react-bridge/wrappers/navigation';

export function useConnectionStatusQuery() {
  return useQuery({
    queryKey: [
      QueryKeyConstants.TikTokAdsAccount,
      QueryKeyConstants.TikTokAdsAccountConnectionStatus,
    ],
    queryFn: async ({ signal }) => {
      return getConnectionStatus({ signal });
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export function useOAuthURLQuery() {
  return useQuery({
    queryKey: [
      QueryKeyConstants.TikTokAdsAccount,
      QueryKeyConstants.TikTokAdsAccountOauthURL,
    ],
    queryFn: async ({ signal }) => {
      const data = await getOAuthUrl({ signal });
      return data.url;
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export function useConnectMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ authCode, state }: { authCode: string; state: string }) => {
      return connect({ authCode, state });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeyConstants.TikTokAdsAccount],
      });
      navigateToState('account-settings.social', {});
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'TikTok Ads Manager account successfully connected!',
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Connecting To TikTok Ads Manager Account Failed',
          message:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
}

export function useRevokeTokenMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => {
      return revokeToken();
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeyConstants.TikTokAdsAccount],
      });
      navigateToState('account-settings.social', {});
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'TikTok Ads Account Disconnected',
          message: 'TikTok Ads Manager account was successfully disconnected.',
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'TikTok Ads Account Disconnect Failed',
          message:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
}

export function useReconnectMutation() {
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: (oauthEndpoint: string) => {
      return revokeToken();
    },
    onSuccess: (data, oauthEndpoint) => {
      window.location.href = oauthEndpoint;
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Reconnecting To TikTok Ads Manager Account Failed',
          message:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
}
